import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTabGroup } from '@angular/material/tabs';
import { faFileExport, faFileImport, faSave } from '@fortawesome/free-solid-svg-icons';
import { FileUploadModule, FileUploader } from 'ng2-file-upload';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Box } from 'src/app/models/box';
import { BoxAccessibility } from 'src/app/models/box-accessibility';
import { Group } from 'src/app/models/group';
import { User } from 'src/app/models/user';
import { BoxesService } from 'src/app/services/boxes.service';
import { DefaultHttpService } from 'src/app/services/default-http.service';
import { GroupsService } from 'src/app/services/groups.service';
import { MemoryService } from 'src/app/services/memory.service';
import { UsersService } from 'src/app/services/users.service';
import { UserAndGroupAccessibilitiesSelectionComponent } from '../../../controls/user-and-group-accessibilities-selection/user-and-group-accessibilities-selection.component';

@Component({
    selector: 'app-box-accessibility',
    templateUrl: './box-accessibility.component.html',
    styleUrls: ['./box-accessibility.component.scss'],
    imports: [UserAndGroupAccessibilitiesSelectionComponent, MatButtonModule, FileUploadModule]
})
export class BoxAccessibilityComponent extends DefaultHttpService implements OnInit {
    public faSave = faSave;
    public faFileExport = faFileExport;
    public faFileimport = faFileImport;

    _box: Box;

    @Input()
    public set box(value: Box) {
        this.loading = true;
        this._box = value;
        this.loadInformations();
        this.processAccessibilities();
    }
    public get box(): Box {
        return this._box;
    }

    /**
     * Valeur indiquant si le formulaire est disabled.
     */
    @Input()
    public disabled: boolean;

    @Output()
    public accessibilitiesUpdated: EventEmitter<BoxAccessibility[]> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    private accessibilitiesToSave: BoxAccessibility[];

    users: User[];

    unusedUsers: User[];

    groups: Group[];

    unusedGroups: Group[];

    accessibilities: BoxAccessibility[];

    public originalAccessibilities: BoxAccessibility[];

    inheritedAccessibilities: BoxAccessibility[];

    /**
     * Valeur indiquant que les données sont en cours de chargement.
     */
    public loading = true;

    // Accès au enfant de type fileToUpload
    @ViewChild('fileToUpload', { static: true })
    fileToUploadVariable: ElementRef;

    // Composant FileUploaderCustom
    public uploader: FileUploader = null;

    @ViewChild(MatTabGroup) tabGroup: MatTabGroup;

    constructor(
        private boxesService: BoxesService,
        private usersService: UsersService,
        private groupsService: GroupsService,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    ngOnInit(): void {
        this.uploader = new FileUploader({
            url: `${this.baseUrl}/api/organizations/${this.organizationId}/boxes/${this._box.id}/Accessibilities/import`,
            authTokenHeader: 'Authorization',
            authToken: `Bearer ${this.connectedUser.token.accessToken}`,
            allowedFileType: ['xls'],
            queueLimit: 1,
        });

        this.uploader.onAfterAddingFile = (fileItem) => {
            fileItem.withCredentials = false;
        };

        this.uploader.onCompleteAll = () => {
            this.loadInformations();
        };
    }

    public onFileSelected() {
        this.uploader.uploadAll();
    }

    loadInformations(): void {
        this.boxesService.getAccessibilities(this._box.id).subscribe((x) => {
            this.accessibilities = x;
            this.originalAccessibilities = x;

            // on récupère tous les utilisateurs
            this.usersService.List(null, null, null, null).subscribe((x) => {
                this.users = x.value.sort((a, b) => {
                    return a.displayName < b.displayName ? -1 : 1;
                });
                this.determinedUnusedUsers();
                this.processAccessibilities();
                this.loading = false;
            });

            // on récupère les groupes
            this.groupsService.List(null, null, null, null).subscribe((x) => {
                this.groups = x.value.sort((a, b) => {
                    return a.name < b.name ? -1 : 1;
                });
                this.determinedUnusedGroups();
                this.processAccessibilities();
                this.loading = false;
            });
        });
    }

    /**
     * Determine les utilisateurs qui n'ont pas accès à la bannette
     */
    determinedUnusedUsers() {
        this.unusedUsers = [...this.users];
        // Ne garde que les utilisateurs pour lesquelles il n'existe pas d'accès.
        this.unusedUsers = this.unusedUsers.filter((a) => this.accessibilities.some((z) => z.userId === a.id) === false);
    }

    /**
     * Determine les groupes qui n'ont pas accès à la bannette
     */
    determinedUnusedGroups() {
        this.unusedGroups = [...this.groups];
        // Ne garde que les groupes pour lesquelles il n'existe pas d'accès.
        this.unusedGroups = this.unusedGroups.filter((a) => this.accessibilities.some((z) => z.groupId === a.id) === false);
    }

    /**
     * Determine les groupes et utilisateur qui ont accès à la bannette
     */
    processAccessibilities() {
        if (this.groups && this.users && this.accessibilities) {
            this.accessibilities.forEach((x) => {
                if (x.userId) {
                    let user = this.users.find((y) => y.id === x.userId);
                    x.userName = user.displayName;
                }

                if (x.groupId) {
                    let group = this.groups.find((y) => y.id === x.groupId);
                    x.groupName = group.name;
                }
            });

            this.sortAccessibilities();
        }
    }

    private sortAccessibilities() {
        let sortedAccessibilities = this.accessibilities.sort((a, b) => {
            if (a.groupName && b.groupName) {
                return a.groupName < b.groupName ? -1 : 1;
            } else if (a.userName && b.userName) {
                return a.userName < b.userName ? -1 : 1;
            } else if (a.groupName) {
                return -1;
            } else {
                return 1;
            }
        });

        this.accessibilities = [...sortedAccessibilities];
    }

    public save(): void {
        this.accessibilitiesUpdated.emit(this.accessibilities);
    }

    public onExported() {
        this.boxesService
            .exportAccessibilities(this._box.id)
            .pipe(
                catchError((err) => {
                    console.error('Export Error');
                    return of(null);
                })
            )
            .subscribe((blob) => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = 'Accessibilité des bannettes.xlsx';
                a.click();
                URL.revokeObjectURL(objectUrl);
            });
    }

    onAccessibilitiesUpdated(event) {
        this.accessibilities = event;
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
